import {
  BADGE_IMAGES,
  GET_ABILITIES_BADGES,
  GET_SKILLS_BADGES,
} from 'api';
import { useLazyQuery } from '@apollo/client';
import { useMemo } from 'react';
import {
  IAbilitiesBadgeDataType,
  IBadgeImage,
  ISkillsBadgeDataType,
} from '../../components/Retention/components/Location/Path/types';

interface IQueryVariables {
  orderBy?: string;
  direction?: string;
  limit: number;
  offset?: number;
  phrase?: string;
}

export default function useBadgesQuery({
  abilityBadgeVars = {},
  skillBadgeVars = {},
}: {
  abilityBadgeVars: IQueryVariables;
  skillBadgeVars: IQueryVariables;
}) {
  const [
    fetchAbilityBadges,
    { data: abilityBadgeData, loading: abilitiesBadgesLoading },
  ] = useLazyQuery(GET_ABILITIES_BADGES, {
    variables: abilityBadgeVars,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });

  const abilityMemoization: IAbilitiesBadgeDataType[] = useMemo(
    () => abilityBadgeData?.getAbilitiesBadges?.results || [],
    [abilityBadgeData?.getAbilitiesBadges?.results],
  );

  const [
    fetchSkillsBadges,
    { data: skillsBadgeData, loading: skillsBadgesLoading },
  ] = useLazyQuery(GET_SKILLS_BADGES, {
    variables: skillBadgeVars,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });

  const skillsMemoization: ISkillsBadgeDataType[] = useMemo(
    () => skillsBadgeData?.getSkillsBadges?.results || [],
    [skillsBadgeData?.getSkillsBadges?.results],
  );

  const [
    fetchBadgeImages,
    { data: badgeImages, loading: badgeImagesLoading },
  ] = useLazyQuery(BADGE_IMAGES, {
    fetchPolicy: 'cache-first',
  });

  const badgeImageMemoization: IBadgeImage[] = useMemo(
    () => badgeImages?.badgeImages || [],
    [badgeImages?.badgeImages],
  );

  return useMemo(
    () => ({
      fetchAbilityBadges,
      abilitiesBadges: abilityMemoization,
      abilitiesBadgesLoading,
      fetchSkillsBadges,
      skillsBadges: skillsMemoization,
      skillsBadgesLoading,
      fetchBadgeImages,
      badgeImages: badgeImageMemoization,
      badgeImagesLoading,
    }),
    [
      fetchAbilityBadges,
      abilityMemoization,
      abilitiesBadgesLoading,
      fetchSkillsBadges,
      skillsMemoization,
      skillsBadgesLoading,
      fetchBadgeImages,
      badgeImages,
      badgeImagesLoading,
    ],
  );
}
